import type { SupportTicketId } from "@redotech/effect-api-shared/ids/definitions";
import { SupportTicketId as SupportTicketIdSchema } from "@redotech/effect-api-shared/ids/definitions";
import type { SupportTicketListItemResponse as SupportTicketListItemResponseType } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import {
  SupportTicketListItemResponse,
  type UpdateSupportTicketInput,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import type { EffectApiContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { BasicIndex } from "@tanstack/db";
import { queryCollectionOptions } from "@tanstack/query-db-collection";
import { createCollection } from "@tanstack/react-db";
import type { QueryClient } from "@tanstack/react-query";
import { Option } from "effect";
import { catchApiErrors } from "./error-handling";

const FIVE_MINUTES_MS = 5 * 60 * 1000;
export const DEFAULT_PAGE_SIZE = 50;

/** Number of pages to prefetch ahead of the current page. */
export const PREFETCH_PAGES_AHEAD = 3;

function toOptionIfPresent<T>(
  value: T | null | undefined,
): Option.Option<T> | undefined {
  if (value === undefined) {
    return undefined;
  }
  return value === null ? Option.none() : Option.some(value);
}

function toUpdateSupportTicketInput(
  changes: Partial<SupportTicketListItemResponseType>,
): UpdateSupportTicketInput {
  return {
    status: changes.status,
    priority: toOptionIfPresent(changes.priority),
    readAt: toOptionIfPresent(changes.readAt),
    mongoAssigneeId: toOptionIfPresent(changes.mongoAssigneeId),
    mongoCustomerId: toOptionIfPresent(changes.mongoCustomerId),
    tagUpdate: changes.tagIds
      ? { _tag: "replace", tagIds: changes.tagIds }
      : undefined,
    snoozedUntil: changes.snoozedUntil,
  };
}

export const supportTicketListQueryKeys = {
  list: () => ["supportTickets", "list"] as const,
};

export async function fetchSingleSupportTicket(
  effectApi: EffectApiContext,
  supportTicketId: SupportTicketId,
): Promise<SupportTicketListItemResponseType> {
  const client = effectApi.getClient();
  return effectApi.runPromise(
    catchApiErrors(
      client.supportTicket.getSupportTicket({ path: { id: supportTicketId } }),
    ),
  );
}

export interface CreateSupportTicketListCollectionOptions {
  queryClient: QueryClient;
  effectApi: EffectApiContext;
}

export interface PageLoadResult {
  items: SupportTicketListItemResponseType[];
  totalCount: number;
  page: number;
}

export function serializeSupportTicket(
  supportTicket: SupportTicketListItemResponseType,
): typeof SupportTicketListItemResponse.Encoded {
  return {
    ...supportTicket,
    readAt: supportTicket.readAt?.toISOString() ?? null,
    lastCustomerMessageAt:
      supportTicket.lastCustomerMessageAt?.toISOString() ?? null,
    lastMerchantMessageAt:
      supportTicket.lastMerchantMessageAt?.toISOString() ?? null,
    createdAt: supportTicket.createdAt?.toISOString() ?? null,
    statusUpdatedAt: supportTicket.statusUpdatedAt?.toISOString() ?? null,
    snoozedUntil: supportTicket.snoozedUntil?.toISOString() ?? null,
  };
}

export function createSupportTicketListCollection({
  queryClient,
  effectApi,
}: CreateSupportTicketListCollectionOptions) {
  const collection = createCollection(
    queryCollectionOptions({
      queryKey: supportTicketListQueryKeys.list(),
      syncMode: "eager",
      autoIndex: "eager",
      defaultIndexType: BasicIndex,
      // Pages are loaded through React Query and upserted into this collection.
      queryFn: async (): Promise<SupportTicketListItemResponseType[]> => [],
      getKey: (item) => item.id,
      queryClient,
      staleTime: FIVE_MINUTES_MS,
      onUpdate: async ({ transaction }) => {
        const client = effectApi.getClient();

        for (const mutation of transaction.mutations) {
          const payload = toUpdateSupportTicketInput(mutation.changes);
          try {
            await effectApi.runPromise(
              catchApiErrors(
                client.supportTicket.updateSupportTicket({
                  path: { id: SupportTicketIdSchema.make(mutation.key) },
                  payload,
                }),
              ),
            );
          } catch (err) {
            console.error(
              "[supportTickets] PATCH failed for",
              mutation.key,
              err,
            );
          }
        }

        return { refetch: false };
      },
    }),
  );

  collection.startSyncImmediate();

  const fetchPage = async (
    page: number,
    filterGroup: string | undefined,
  ): Promise<PageLoadResult> => {
    const offset = page * DEFAULT_PAGE_SIZE;
    const client = effectApi.getClient();

    const result = await effectApi.runPromise(
      catchApiErrors(
        client.supportTicket.listSupportTickets({
          urlParams: { limit: DEFAULT_PAGE_SIZE, offset, filterGroup },
        }),
      ),
    );

    collection.utils.writeBatch(() => {
      for (const item of result.items) {
        collection.utils.writeUpsert(item);
      }
    });

    return { items: [...result.items], totalCount: result.totalCount, page };
  };

  return { collection, fetchPage };
}

export type SupportTicketListCollectionWithLoader = ReturnType<
  typeof createSupportTicketListCollection
>;
